import "./VerifiedBadge.css";

// The verified-seller seal: a scalloped disc in --verified-green with a white
// tick, drawn from the owner's artwork (2026-10-17) as an inline SVG rather
// than the 608 KB source PNG. It shows at ~1em beside a shop name, where the
// source's ~30 tiny scallops would blur into a fuzzy circle, so it uses 18
// shallow ones: a seal at 13px, and close to the artwork when enlarged.
// (12 deeper bumps were tried first and read as a flower.)
//
// Colour, measured: --verified-green is 5.83:1 against the page background
// and the white tick is 3.39:1 on it (WCAG asks 3:1 for graphics). The
// artwork's own #026C49 was 3.05:1 against the page, too close to the line
// over the dot-wave background.

const SCALLOPS = 18;
const DISC_RADIUS = 10.5;
const BUMP_RADIUS = 1.4;
const BUMP_DISTANCE = 10.4;

// Computed once: the bump centres around (12, 12).
const BUMPS = Array.from({ length: SCALLOPS }, (_, i) => {
  const angle = (2 * Math.PI * i) / SCALLOPS;
  return {
    cx: +(12 + BUMP_DISTANCE * Math.cos(angle)).toFixed(3),
    cy: +(12 + BUMP_DISTANCE * Math.sin(angle)).toFixed(3),
  };
});

// `size` is a CSS length ("64px", "1.5rem"); by default the seal is 1.15em,
// matching the text beside it. At 48px and above the seal gains a thin
// inner ring, the artwork's detail, which would only blur at text size.
export const DETAIL_RING_MIN_PX = 48;

const pxOf = (size) => {
  if (typeof size === "number") return size;
  const m = /^(\d+(?:\.\d+)?)px$/.exec(String(size || ""));
  return m ? Number(m[1]) : null;
};

const VerifiedBadge = ({ label = "Emporia verified seller", className = "", size }) => {
  const px = pxOf(size);
  const style = size ? { width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size } : undefined;
  const ring = px != null && px >= DETAIL_RING_MIN_PX;
  return (
  <svg
    className={`verified-badge${className ? ` ${className}` : ""}`}
    style={style}
    viewBox="0 0 24 24"
    role="img"
    aria-label={label}
    focusable="false"
  >
    <title>{label}</title>
    <g className="verified-badge-seal">
      <circle cx="12" cy="12" r={DISC_RADIUS} />
      {BUMPS.map(({ cx, cy }) => (
        <circle key={`${cx},${cy}`} cx={cx} cy={cy} r={BUMP_RADIUS} />
      ))}
    </g>
    {ring && <circle className="verified-badge-ring" cx="12" cy="12" r="8.6" data-testid="verified-badge-ring" />}
    {/* A short stroke down, then a long rising curve: the artwork's tick. */}
    <path className="verified-badge-tick" d="M7.3 12.6 L10.4 15.8 Q13 11.2 17.2 8.2" />
  </svg>
  );
};

export default VerifiedBadge;
